import {
  fetchAuthSession,
  getCurrentUser,
  signInWithRedirect,
  signOut,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { AuthError, buildAuthorizeUrlForRetry, issueCookie } from './auth';
import { ensureAmplifyConfigured, getConfig } from './config';

const DOMAIN_ERROR_KEY = 'doc_server_domain_error';

function showStatus(message: string): void {
  const el = document.getElementById('status');
  if (el) {
    el.textContent = message;
  }
}

function showError(message: string): void {
  const el = document.getElementById('status');
  if (el) {
    el.textContent = message;
    el.style.color = '#c00';
  }
}

/**
 * Detect which page the user originally wanted by inspecting the current URL.
 * CloudFront ErrorResponse serves /public/login/index.html with the original
 * request URI preserved in the browser address bar.
 */
function getReturnTo(): string {
  const { pathname, search, hash } = window.location;
  if (pathname.startsWith('/public/login')) {
    return '/';
  }
  return pathname + search + hash;
}

function showRetryAction(returnTo: string): void {
  const actionEl = document.getElementById('login-action');
  const btnEl = document.getElementById('login-btn');
  if (actionEl) {
    actionEl.style.display = 'block';
  }
  if (btnEl) {
    btnEl.textContent = 'Log in with a different account';
    btnEl.onclick = async () => {
      sessionStorage.removeItem(DOMAIN_ERROR_KEY);
      sessionStorage.setItem('doc_server_return_to', returnTo);
      try {
        await signOut();
      } catch {
        // Ignore — local session may already be cleared
      }
      const config = getConfig();
      window.location.href = buildAuthorizeUrlForRetry(config, returnTo);
    };
  }
}

async function handleAuthenticated(returnTo: string): Promise<void> {
  showStatus('Logging in...');
  const { tokens } = await fetchAuthSession();
  if (!tokens?.idToken) {
    throw new Error('ID token not available');
  }

  await issueCookie(tokens.idToken.toString());

  showStatus('Login complete. Redirecting...');
  const sep = returnTo.includes('?') ? '&' : '?';
  window.location.href = `${returnTo}${sep}_=${Date.now()}`;
}

/**
 * Show domain-not-allowed error with a retry button.
 * Sets a sessionStorage flag to prevent auto-redirect on subsequent page loads.
 */
function showDomainError(): void {
  sessionStorage.setItem(DOMAIN_ERROR_KEY, '1');
  showError(
    'Access is not allowed with this email domain. Please log in with your organization account.'
  );
  showRetryAction(getReturnTo());
}

function showLoginButton(returnTo: string): void {
  const statusEl = document.getElementById('status');
  const actionEl = document.getElementById('login-action');
  const btnEl = document.getElementById('login-btn');
  if (statusEl) {
    statusEl.textContent = 'To log in again, please press the button below.';
  }
  if (actionEl) {
    actionEl.style.display = 'block';
  }
  if (btnEl) {
    btnEl.addEventListener('click', () => {
      sessionStorage.removeItem('doc_server_from_logout');
      signInWithRedirect({ provider: 'Google', customState: returnTo }).catch(
        (err: unknown) => console.error('signInWithRedirect failed:', err)
      );
    });
  }
}

/** Stored returnTo from customOAuthState Hub event. */
let pendingReturnTo: string | null = null;
/** Guard to prevent double handleAuthenticated calls (Hub event + getCurrentUser race). */
let authHandled = false;

function safeHandleAuthenticated(returnTo: string): void {
  if (authHandled) {
    return;
  }
  authHandled = true;
  handleAuthenticated(returnTo).catch((err: unknown) => {
    authHandled = false;
    console.error('Login failed:', err);

    if (err instanceof AuthError && err.statusCode === 403) {
      showDomainError();
      return;
    }

    showError(
      `Login failed: ${err instanceof Error ? err.message : String(err)}`
    );
  });
}

function init(): void {
  ensureAmplifyConfigured();

  const params = new URLSearchParams(window.location.search);
  const isOAuthError = params.has('error');

  const hasDomainError = sessionStorage.getItem(DOMAIN_ERROR_KEY) === '1';

  Hub.listen('auth', ({ payload }) => {
    if (payload.event === 'customOAuthState') {
      pendingReturnTo = payload.data;
    }

    if (payload.event === 'signInWithRedirect') {
      const returnTo =
        pendingReturnTo ??
        sessionStorage.getItem('doc_server_return_to') ??
        '/';
      sessionStorage.removeItem('doc_server_return_to');
      safeHandleAuthenticated(returnTo);
    }

    if (payload.event === 'signInWithRedirect_failure') {
      console.error('signInWithRedirect failed:', payload.data);
      showDomainError();
    }
  });

  getCurrentUser()
    .then(() => {
      if (hasDomainError) {
        showDomainError();
        return;
      }
      const returnTo =
        sessionStorage.getItem('doc_server_return_to') ?? getReturnTo();
      sessionStorage.removeItem('doc_server_return_to');
      safeHandleAuthenticated(returnTo);
    })
    .catch(() => {
      if (isOAuthError || hasDomainError) {
        showDomainError();
        return;
      }

      const returnTo = getReturnTo();
      const fromLogout =
        params.get('from') === 'logout' ||
        sessionStorage.getItem('doc_server_from_logout') === '1';

      if (params.has('code')) {
        showStatus('Logging in...');
        return;
      }

      if (fromLogout) {
        showLoginButton(returnTo);
      } else {
        signInWithRedirect({ provider: 'Google', customState: returnTo }).catch(
          (err: unknown) => console.error('signInWithRedirect failed:', err)
        );
      }
    });
}

init();
